import { Reference } from '@approvalmax/types';
import { DateTimeEditor, DropdownEditor, Field, TextEditor } from '@approvalmax/ui';
import { Flex, Tooltip } from '@approvalmax/ui/src/components';
import { constants } from 'modules/common';
import { domain } from 'modules/data';
import { PlainDataProvider } from 'modules/data-providers';
import { useSelector } from 'modules/react-redux';
import { InfoIcon } from 'modules/sprites';
import {
    changeQbooksDate,
    changeQbooksDepartment,
    changeQbooksDueDate,
    changeQbooksNumber,
    changeQbooksTerms,
} from 'pages/requestForm/actions';
import { SectionContainer } from 'pages/requestForm/components/SectionContainer';
import { getRequest, getShowValidationErrors } from 'pages/requestForm/selectors/pageSelectors';
import { getIsRequiredLocation, getQBooksContext } from 'pages/requestForm/selectors/qbooks';
import { memo, useCallback, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';

import { getDepartmentsWithNesting, QBooksDepartment } from '../../../../../data/qbooks/QBooksDepartment';
import { messages } from './SalesInvoiceFieldsSection.messages';

export const SalesInvoiceFieldsSection = memo(() => {
    const dispatch = useDispatch();

    const request = useSelector<domain.QBooksInvoiceRequest>(getRequest);
    const qBooksContext = useSelector(getQBooksContext);
    const showValidationErrors = useSelector(getShowValidationErrors);
    const isRequiredLocation = useSelector((state) => getIsRequiredLocation(state, request));

    const { details } = request;
    const { number: invoiceNumber = '', date: invoiceDate, department, dueDate, term } = details;
    const isCustomTransactionNumberEnabled = useRef(
        qBooksContext.salesFormOptionalFields?.customTransactionNumberEnabled || invoiceNumber
    );
    const isDepartmentTrackingEnabled = qBooksContext.hasDepartmentFeature;

    const handleTermsChange = useCallback((value: domain.QBooksTerm) => dispatch(changeQbooksTerms(value)), [dispatch]);

    const handleInvoiceDateChange = useCallback((value: string) => dispatch(changeQbooksDate(value)), [dispatch]);

    const handleDueDateChange = useCallback((value: string) => dispatch(changeQbooksDueDate(value)), [dispatch]);

    const handleInvoiceNumberChange = useCallback((value: string) => dispatch(changeQbooksNumber(value)), [dispatch]);

    const handleDepartmentChange = useCallback(
        (value: Reference) => dispatch(changeQbooksDepartment(value)),
        [dispatch]
    );

    const departments = useMemo(() => {
        return getDepartmentsWithNesting(qBooksContext.departments || []);
    }, [qBooksContext.departments]);

    return (
        <SectionContainer title={messages.sectionTitle}>
            <Flex container direction='column' spacing='8 0'>
                <Flex container spacing='20'>
                    <Flex size={6} block>
                        <Field title={messages.termsField}>
                            <PlainDataProvider items={qBooksContext.terms}>
                                <DropdownEditor
                                    value={term}
                                    onChange={handleTermsChange}
                                    placeholder={messages.termsPlaceholder}
                                />
                            </PlainDataProvider>
                        </Field>
                    </Flex>

                    <Flex size={6} block>
                        <Field required title={messages.invoiceDateField}>
                            <DateTimeEditor
                                invalid={showValidationErrors && !invoiceDate}
                                value={invoiceDate}
                                onChange={handleInvoiceDateChange}
                                placeholder={messages.invoiceDatePlaceholder}
                            />
                        </Field>
                    </Flex>

                    <Flex size={6} block>
                        <Field required title={messages.dueDateField}>
                            <DateTimeEditor
                                invalid={showValidationErrors && !dueDate}
                                value={dueDate}
                                onChange={handleDueDateChange}
                                placeholder={messages.dueDatePlaceholder}
                            />
                        </Field>
                    </Flex>
                </Flex>

                {(isCustomTransactionNumberEnabled.current || isDepartmentTrackingEnabled) && (
                    <Flex container spacing='20'>
                        {isCustomTransactionNumberEnabled.current && (
                            <Flex size={6} block>
                                <Field
                                    title={
                                        <Flex alignItems='center' spacing='0 4'>
                                            {messages.invoiceNumberField}

                                            <Tooltip
                                                width={300}
                                                placement='top'
                                                display='flex'
                                                activator={<InfoIcon width={10} height={10} color='midnight70' />}
                                            >
                                                {messages.customTransactionNumberTooltip}
                                            </Tooltip>
                                        </Flex>
                                    }
                                >
                                    <TextEditor
                                        value={invoiceNumber}
                                        onChange={handleInvoiceNumberChange}
                                        maxLength={constants.qBooksConstants.BILL_NUMBER_MAX_LENGTH}
                                        placeholder={messages.invoiceNumberPlaceholder}
                                        changeOnBlur
                                    />
                                </Field>
                            </Flex>
                        )}

                        {isDepartmentTrackingEnabled && (
                            <Flex size={6} block>
                                <Field title={qBooksContext.departmentField?.text} required={isRequiredLocation}>
                                    <PlainDataProvider items={departments}>
                                        <DropdownEditor
                                            invalid={showValidationErrors && isRequiredLocation && !department}
                                            value={department}
                                            onChange={handleDepartmentChange}
                                            placeholder={messages.departmentPlaceholder({
                                                fieldText: qBooksContext.departmentField?.text,
                                            })}
                                            listItem={(props) => (
                                                <DropdownEditor.ListItem
                                                    {...props}
                                                    nestingLevel={(props.item as QBooksDepartment).nestingLevel}
                                                />
                                            )}
                                        />
                                    </PlainDataProvider>
                                </Field>
                            </Flex>
                        )}
                    </Flex>
                )}
            </Flex>
        </SectionContainer>
    );
});

SalesInvoiceFieldsSection.displayName = 'SalesInvoiceFieldsSection';
